import { lazy } from "react";
import {
  Route,
  Navigate,
  useLocation,
  Routes as RouterRoutes,
} from "react-router-dom";

// Constants
import { supportedLanguages } from "../constants/common";

// Landing components
const Blog = lazy(() => import("../pages/blog/Blog"));
const Blogs = lazy(() => import("../pages/blog/Blogs"));
const About = lazy(() => import("../pages/about/About"));
const Contact = lazy(() => import("../pages/contact/Contact"));
const Landing = lazy(() => import("../pages/landing/Landing"));
const Services = lazy(() => import("../pages/services/Services"));

// Auth components
const Login = lazy(() => import("../pages/auth/Login"));
const Register = lazy(() => import("../pages/auth/Register"));

const VerifyEmail = lazy(() => import("../pages/auth/VerifyEmail"));
const ResetPassword = lazy(() => import("../pages/auth/ResetPassword"));
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));

// Error components
const NotFound = lazy(() => import("../pages/errors/NotFound"));
const Maintenance = lazy(() => import("../pages/errors/Maintenance"));

// Legal components
const PrivacyPolicy = lazy(() => import("../pages/legal/PrivacyPolicy"));
const TermsConditions = lazy(() => import("../pages/legal/TermsConditions"));

// Public resources components
const PublicSchemesOfWork = lazy(() => import("../pages/public/PublicSchemesOfWork"));
const PublicExamRegistrationForms = lazy(() => import("../pages/public/PublicExamRegistrationForms"));

// Other components
import AuthRoutes from "../components/auth/AuthRoute";
import PersistLogin from "../components/auth/PersistLogin";
import PrivateRoute from "../components/auth/PrivateRoute";

// Admin routes components
import AdminRoutes from "./AdminRoutes";
import SuperAdminRoutes from "./SuperAdminRoutes";
import SchoolAdminRoutes from "./SchoolAdminRoutes";

const LanguageLayout = () => (
  <RouterRoutes>
    {/* Public routes */}
    <Route path="/" element={<Landing />} />
    <Route path="about" element={<About />} />
    <Route path="services" element={<Services />} />
    <Route path="blog">
      <Route index element={<Blogs />} />
      <Route path=":slug" element={<Blog />} />
    </Route>
    <Route path="contact" element={<Contact />} />
    <Route path="legal">
      <Route path="privacy-policy" element={<PrivacyPolicy />} />
      <Route path="terms-conditions" element={<TermsConditions />} />
    </Route>

    <Route path="public">
      <Route path="schemes-of-work" element={<PublicSchemesOfWork />} />
      <Route path="exam-registration-forms" element={<PublicExamRegistrationForms />} />
    </Route>

    {/* Error routes */}
    <Route path="maintenance" element={<Maintenance />} />

    {/* Auth routes - only accessible when not authenticated */}
    <Route element={<AuthRoutes />}>
      <Route path="auth">
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route index element={<Navigate to="login" replace />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
      </Route>
    </Route>

    {/* Protected routes - only accessible when authenticated */}
    <Route element={<PersistLogin />}>
      {/* Super admin routes */}
      <Route element={<PrivateRoute allowedRoles={["super-admin"]} />}>
        <Route path="super-admin/*" element={<SuperAdminRoutes />} />
      </Route>

      {/*admin routes */}
      <Route element={<PrivateRoute allowedRoles={["admin"]} />}>
        <Route path="admin/*" element={<AdminRoutes />} />
      </Route>

      {/*school admin routes */}
      <Route element={<PrivateRoute allowedRoles={["school-admin"]} />}>
        <Route path="school-admin/*" element={<SchoolAdminRoutes />} />
      </Route>
    </Route>

    {/* Catch-all for invalid paths under a language prefix */}
    <Route path="*" element={<NotFound />} />
  </RouterRoutes>
);

const AppRoutes = () => {
  const location = useLocation();

  const getLanguageFromPath = (path: string) => {
    const pathArray = path.split("/");
    return pathArray[1] || "";
  };

  const pathLang = getLanguageFromPath(location.pathname);
  const storedLang = localStorage.getItem("lang") || "en";
  const currentLang = supportedLanguages.includes(storedLang)
    ? storedLang
    : "en";

  // Handle unsupported languages by reconstructing the path with the fallback language
  if (!supportedLanguages.includes(pathLang)) {
    const pathAfterLang = location.pathname.split("/").slice(2).join("/") || "";

    return (
      <Navigate
        to={`/${currentLang}/${pathAfterLang}${location.search}`}
        replace
      />
    );
  }

  return (
    <RouterRoutes>
      {/* Routes with language prefix */}
      <Route path="/:lng/*" element={<LanguageLayout />} />
      {/* Catch-all for root-level invalid paths */}
      <Route path="*" element={<Navigate to={`/${currentLang}`} replace />} />
    </RouterRoutes>
  );
};

export default AppRoutes;
